import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Portfolio access request dialog. Buyer submits proof-of-funds + company
 * + contact info before the seller unlocks individual note documents.
 */
export default function RequestAccessDialog({ portfolio, open, onOpenChange, onSubmitted }) {
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [proof, setProof] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!portfolio) return null;

  const submit = async () => {
    if (!companyName.trim() || !contactName.trim() || !contactEmail.trim() || !proof.trim()) {
      toast.error("Company, contact name, contact email and proof of funds are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/portfolios/${portfolio.portfolio_id}/access-requests`, {
        company_name: companyName,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone || null,
        proof_of_funds: proof,
        message: message || null,
      });
      onSubmitted?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-access-dialog" className="rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Request Portfolio Access</DialogTitle>
          <DialogDescription>
            The seller reviews every access request. Once approved you unlock individual notes, payment histories, contracts, titles, and supporting collateral.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Company Name"><Input data-testid="req-company" className="rounded-none" value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact Name"><Input data-testid="req-contact-name" className="rounded-none" value={contactName} onChange={(e) => setContactName(e.target.value)} /></Field>
            <Field label="Contact Phone (optional)"><Input data-testid="req-contact-phone" className="rounded-none" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></Field>
          </div>
          <Field label="Contact Email"><Input type="email" data-testid="req-contact-email" className="rounded-none" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></Field>
          <Field label="Proof of Funds">
            <Textarea data-testid="req-proof" rows={3} className="rounded-none"
              placeholder="Bank statement snapshot summary, LOC amount, or fund manager reference. The seller will review this before unlocking documents."
              value={proof} onChange={(e) => setProof(e.target.value)} />
          </Field>
          <Field label="Message (optional)">
            <Textarea data-testid="req-message" rows={2} className="rounded-none" value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="req-cancel" className="rounded-none uppercase text-xs tracking-wider" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="req-submit" onClick={submit} disabled={busy} className="rounded-none uppercase text-xs tracking-wider">
            {busy ? "Submitting…" : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-overline mb-1 block text-xs">{label}</Label>
      {children}
    </div>
  );
}
